<template>
  <div
    class="date-picker"
    :class="{'relative': dateGroup == false}"
    v-on-clickaway="away" @keydown="arrowPress($event)"
  >
    <div
      class="form-group has-suffix"
      :class="[
        inputGroupClass,
        {'has-prefix': !!$slots.prefix, }
      ]"
    >
      <label
        class="form-label"
        role="label"
        id='formLabel'
        :for="id"
        :class="{ 'has-error ': error }"
        v-if='label'
      >
        {{ label  }}
      </label>
      <div class="form-group__panel">
        <span class="form-control__prefix" id='formControlPrefix'>
          <slot name="prefix" ></slot>
        </span>
        <input
          :id='id'
          class="form-control"
          :class="[inputClasses, { 'has-error ': error }]"
          :style="inputStyles"
          type="text"
          v-bind="$attrs"
          v-model='showDate'
          aria-labelledby='formControlPrefix'
          :readonly="readonly"
          autocomplete="off"
          @focus="onFocus()"
          ref='input'
        />
        <span class="form-control__suffix">
          <slot name="suffix" v-if='!!$slots.suffix'></slot>
          <img v-else src="../../../../assets/icons/calender.png" alt='calender icon'>
        </span>
      </div>
      <transition name="slide">
        <span v-show="error" class="form-feedback is-danger">{{ error }}</span>
      </transition>
    </div>
    <transition name="slideUp">
    <div
        class="calendar"
        v-show="open"
        ref='calender'
    >
        <div class="calender-header">
            <div class="calender-header__panel">
                <div class='left'>
                  <div class="month">{{ currentMonth | formatMonth }}</div>
                  <div class="year">{{ currentYear }}</div>
                </div>
                <div class="right">
                  <div class="arrow" @click="changeCurrentMonth(-1)">
                    <img src="../../../../assets/icons/arrow-left.png" alt='arrow left'>
                  </div>
                  <div class="arrow" @click="changeCurrentMonth(1)">
                    <img src="../../../../assets/icons/arrow-right.png" alt='arrow right'>
                  </div>
                </div>
            </div>
        </div>
        <table>
            <thead>
                <tr class="week">
                    <td>Sun</td>
                    <td>Mon</td>
                    <td>Tue</td>
                    <td>Wen</td>
                    <td>Thu</td>
                    <td>Fri</td>
                    <td>Sat</td>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(row, k) in dateMatrix" v-bind:key="k">
                    <td
                        class="day"
                        v-bind:key="i"
                        v-for="(date, i) in row"
                        :class="{
                          disabled: !dateValidations(date),
                          fade: date.getMonth() !== currentMonth,
                          selected: date.valueOf() == pickedDate.valueOf()
                        }"
                        @click="pickDate(date)"
                    >
                        {{ date.getDate() }}
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
    </transition>
</div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import themeSetupMixin from '@/mixins/theme';

export default {
  inheritAttrs: false,
  mixins: [clickaway, themeSetupMixin],
  props: {
    error: String,
    label: String,
    value: [String, Date],
    /**
     * If `true`, the default input value will be set with the current date
    */
    setToday: {
      type: Boolean,
      default: false,
    },
    /**
     * ClassNames for input group
    */
    inputGroupClass: {
      type: String,
      default: '',
    },
    /**
     * The minimum date that a user can select
    */
    minDate: Date,
    /**
     * If `false`, removes readonly but by default, readonly is set to true
    */
    readonly: {
      type: Boolean,
      default: true,
    },
    /**
     * ClassNames for input
    */
    inputClasses: {
      type: String,
      default: '',
    },
    /**
     * Inline Styles for input
    */
    inputStyles: {
      type: String,
      default: '',
    },
    /**
     * If `true`, add custom date group class to date picker for proper customization
    */
    dateGroup: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const date = new Date();
    return {
      open: false,
      pickedDate: date,
      currentYear: date.getFullYear(),
      currentMonth: date.getMonth(),
      currentDay: date.getDate(),
    };
  },
  mounted() {
    if (!this.value && !this.setToday) {
      this.pickedDate = '';
    }

    if (this.value) {
      this.pickedDate = this.value;
      this.updatePickedDate(this.value);
    }
  },
  computed: {
    /**
       * Generate unique Id for input
       * @function id
       * @return {Strin}
    */
    id() {
      return 'dateInput_' + Math.random().toString(36).substr(2, 9);
    },
    showDate: {
      /**
       * get current date from picked date
       * @function get
       * @return {Date(XXXX-XX-XX)}
      */
      get() {
        const date = this.pickedDate;
        if (date instanceof Date) {
          // format date as (XXXX-XX-XX)
          const year = date.getFullYear();
          const month = (date.getMonth() < 9 ? '0' + (date.getMonth() + 1) : date.getMonth());
          const day = (date.getDate() < 10 ? '0' + date.getDate() : date.getDate());
          return year + '-' + month + '-' + day;
        }
        return date;
      },
      /**
       * Set date
       * @param {String} value
      */
      set(value) {
        if (!this.dateValidations(value)) return;
        this.updatePickedDate(value);
        this.$emit('input', this.showDate);
      },
    },
    /**
     * Check if current year is a Leapyear
     * @return {Boolean}
    */
    leapYear() {
      if (this.currentYear % 100 === 0) {
        return this.currentYear % 400 === 0;
      }
      return this.currentYear % 4 === 0;
    },
    /**
     * Create a 1Dimensional array of dates
     * @return {Array}
    */
    dates() {
      const date1D = [];
      const firstDayOfMonth = new Date(this.currentYear, this.currentMonth, 1);
      const lastDayOfMonth = new Date(
        this.currentYear, this.currentMonth,
        this.getLastDayOfMonth(this.currentMonth),
      );
      date1D.unshift(firstDayOfMonth);

      for (let d = this.prevDate(firstDayOfMonth); d.getDay() !== 6; d = this.prevDate(d)) {
        date1D.unshift(d);
      }
      for (let d = this.nextDate(firstDayOfMonth); d <= lastDayOfMonth; d = this.nextDate(d)) {
        date1D.push(d);
      }
      for (let d = this.nextDate(lastDayOfMonth); d.getDay() !== 0; d = this.nextDate(d)) {
        date1D.push(d);
      }
      return date1D;
    },
    /**
     * Create a 2Dimensional array from dates
     * @return {Array}
    */
    dateMatrix() {
      return this.dates.reduce((matrix, value, i) => {
        if (i % 7 === 0) {
          matrix[matrix.length] = [];
        }
        matrix[matrix.length - 1].push(value);
        return matrix;
      }, []);
    },
  },
  methods: {
    /**
     * Change current month/year by Adding or removing one(1) from the current month/year
     * @param {Integer} month
    */
    changeCurrentMonth(month) {
      const temp = this.currentMonth + month;
      if (temp < 0) {
        this.currentYear -= 1;
      } else if (temp >= 12) {
        this.currentYear += 1;
      }

      this.currentMonth = ((temp) + 12) % 12;
    },
    /**
     * Return next date by Adding 1
     * @param {Date} date
     * @return {Date}
    */
    nextDate(date) {
      const thisDate = new Date(date.valueOf());
      thisDate.setDate(date.getDate() + 1);
      return thisDate;
    },
    /**
     * Return next date by Removing 1
     * @param {Date} date
     * @return {Date}
    */
    prevDate(date) {
      const thisDate = new Date(date.valueOf());
      thisDate.setDate(date.getDate() - 1);
      return thisDate;
    },
    /**
     * Get last day of the month
     * @param {Integer} month Represented as index
     * @return {Integer}
    */
    getLastDayOfMonth(month) {
      const lastDays = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
      if (this.leapYear) {
        lastDays[1] = 29;
      }
      return lastDays[month];
    },
    /**
     * Change current date to picked date
     * @param {Date} date
    */
    pickDate(date) {
      if (!this.dateValidations(date)) return;

      this.pickedDate = date;
      this.currentMonth = date.getMonth();
      this.currentYear = date.getFullYear();
      this.open = false;
      this.$emit('input', this.showDate);
    },
    onFocus() {
      this.open = true;
    },
    away() {
      this.open = false;
    },
    /**
     * Update picked date
     * @param {Date | String (0000-00-00)} value
    */
    updatePickedDate(value) {
      const reg = new RegExp('(\\d{4})-(\\d{2})-(\\d{2})');
      const match = reg.exec(value);
      if (match) {
        const year = parseInt(match[1], 10);
        const month = parseInt(match[2], 10);
        const day = parseInt(match[3], 10);

        if (month > 12 || month < 1) {
          return;
        }
        if (day < 1 || day > this.getLastDayOfMonth(month - 1)) {
          return;
        }
        const date = new Date(match[1], parseInt(match[2], 10) - 1, match[3]);
        if (!this.dateValidations(date)) return;
        this.pickedDate = date;
        this.currentMonth = month - 1;
        this.currentYear = year;
      } else {
        this.pickedDate = value;
      }
    },
    arrowPress(e) {
      if (!this.open) return;
      const event = e || window.event;

      // close date picker when user clicks on tab
      if (event.keyCode === 9) {
        this.open = false;
      }

      if (event.keyCode === 37) {
        this.changeCurrentMonth(-1);
      } else if (event.keyCode === 39) {
        this.changeCurrentMonth(1);
      }
    },
    /**
     * Handles date validation before rendering calender and while interacting with the calender
     * @param {Date} date
    */
    dateValidations(date) {
      if (this.minDate) {
        this.minDate.setHours(0, 0, 0, 0);
        if (date.valueOf() < this.minDate.valueOf()) return false;
      }

      return true;
    },
  },
  filters: {
    /**
     * format month
     * @param {Integer} month
     * @return {String}
    */
    formatMonth(month) {
      return [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ][month];
    },
  },
};
</script>

<style lang='scss'>
@import "../../../../assets/scss/_variables.scss";

.form-group {
  .form-control__suffix {
    img {
      height:auto;
      width:15px;
    }
  }
}
.date-picker {
  display: inline-block;
  width:100%;
  &.relative {
    position: relative;
  }
  @media (max-width: 640px){
    position: relative;
  }

  .calendar {
    background: var(--brand-white);
    border: 1px solid var(--primary-50);
    box-sizing: border-box;
    border-radius: 5px;
    border-radius: 5px;
    padding: 15px;
    position: absolute;
    left:0;
    z-index: 99;
    top: 50px;
    font-family: $primaryFont;
  }

  .calendar:before {
    content: '';
    width: 0;
    height: 0;
    display: none;
    border: solid transparent 8px;
    border-bottom: solid var(--primary-10) 8px;
    position: absolute;
    bottom: 100%;
    left: 1em;
  }
  .calender-header {
    position: relative;
    display: block;
    clear: both;
    min-height: 35px;
    border-bottom: 1px solid var(--primary-50);
    .calender-header__panel {
      position: relative;
      padding:0 5px;
      .left {
        display: flex;
        float: left;
        .month{
          font-weight: 500;
          font-size: 18px;
          line-height: 26px;
          display: flex;
          align-items: center;
          color: var(--text-10);
        }
        .year {
          height: 1.2em;
          padding:10px;
          position: relative;
          top:2px;
          font-weight: normal;
          font-size: 12px;
          line-height: 20px;
          display: flex;
          align-items: center;
          color:  var(--primary-40);
        }
      }
      .right {
        float: right;
        display: flex;
        padding-top:5px;
        .arrow {
          cursor: pointer;
          font-size:1.5rem;
          padding-left:20px;
        }

        .arrow:hover {
          opacity:0.9;
        }
      }
    }
  }

  .fade {
    color: var(--primary-40);
  }

  .center {
    text-align: center;
    clear: both;
  }

  table {
    clear: both;
  }

  table .week td {
    color: var(--text-10);
  }

  table td {
    width: 2em;
    height: 2em;
    vertical-align: middle;
    text-align: center;
    padding:5px;
    font-size: 0.7rem;
  }

  tbody td {
    cursor: pointer;
    border-radius: 100%;
    &.selected {
      background: var(--primary-60);
      color:var(--brand-light);
    }
    &.selected:hover {
      background: var(--primary-60);
      color:var(--brand-light);
    }
    &:hover {
      background-color: var(--primary-60);
      color:var(--brand-light);
    }
    &.disabled {
      color: var(--primary-40);
      cursor: default;
      &:hover{
        background: none;
      }
    }
  }
  .slideUp-enter-active, .slideUp-leave-active {
    transition: .1s;
  }
  .slideUp-enter, .slideUp-leave-to {
    transform: translateY(-10px);
    opacity:0;
  }
}
</style>
